import React, { useRef, useState, useEffect, useCallback } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { sharedCollaborators } from "../../data/collaborators";
import {
  Section,
  SectionHeading,
  HeadingAccent,
  LogoStrip,
  Track,
  LogoCell,
  NavButton,
  CarouselCaption
} from "./styled.components";

const carouselLogos = sharedCollaborators.filter(c => c.showInHomepageCarousel);
const AUTO_ADVANCE_MS = 4000;
const SM_BREAKPOINT = 576;
const LG_BREAKPOINT = 992;

interface Props {
  heading: string;
  accent: string;
  uppercaseHeading: boolean;
  caption?: string;
}

const CollaboratingWithLeaders: React.FC<Props> = ({
  heading,
  accent,
  uppercaseHeading,
  caption
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const [logosPerPage, setLogosPerPage] = useState(4);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const totalPages = Math.ceil(carouselLogos.length / logosPerPage);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w < SM_BREAKPOINT) setLogosPerPage(1);
      else if (w < LG_BREAKPOINT) setLogosPerPage(3);
      else setLogosPerPage(4);
      setPage(0);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const scrollToPage = useCallback((p: number) => {
    if (!trackRef.current) return;
    trackRef.current.scrollTo({
      left: p * trackRef.current.offsetWidth,
      behavior: "smooth"
    });
    setPage(p);
  }, []);

  const goNext = useCallback(
    () => scrollToPage((page + 1) % totalPages),
    [page, totalPages, scrollToPage]
  );

  const goPrev = useCallback(
    () => scrollToPage((page - 1 + totalPages) % totalPages),
    [page, totalPages, scrollToPage]
  );

  useEffect(() => {
    timerRef.current = setInterval(goNext, AUTO_ADVANCE_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [goNext]);

  const handleNav = (fn: () => void) => {
    if (timerRef.current) clearInterval(timerRef.current);
    fn();
    timerRef.current = setInterval(goNext, AUTO_ADVANCE_MS);
  };

  const cellWidth = `calc(100% / ${logosPerPage})`;

  return (
    <Section>
      <SectionHeading $uppercase={uppercaseHeading}>
        {heading}
        {accent && (
          <>
            <br />
            <HeadingAccent>{accent}</HeadingAccent>
          </>
        )}
      </SectionHeading>
      <LogoStrip>
        <NavButton
          onClick={() => handleNav(goPrev)}
          aria-label="Previous partners"
        >
          <FaChevronLeft />
        </NavButton>
        <Track ref={trackRef}>
          {carouselLogos.map(c => (
            <LogoCell
              key={c.id}
              href={c.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ width: cellWidth, flex: `0 0 ${cellWidth}` }}
            >
              <img src={c.logo} alt={c.name} />
            </LogoCell>
          ))}
        </Track>
        <NavButton onClick={() => handleNav(goNext)} aria-label="Next partners">
          <FaChevronRight />
        </NavButton>
      </LogoStrip>
      {caption && <CarouselCaption>{caption}</CarouselCaption>}
    </Section>
  );
};

export default CollaboratingWithLeaders;
